import { State, Action, StateContext, Store } from '@ngxs/store';
import { catchError, tap } from 'rxjs/operators';
import { ShippingItem } from '@shared/models/shipping-item.model';
import { patch, insertItem, updateItem } from '@ngxs/store/operators';
import { Injectable, inject } from '@angular/core';
import { LogisticsService } from '@sales/services/logistics.service';
import { DeliveryOptionDetails } from '../../models/delivery-options.model';
import { SetOrderPaymentTypeAction } from '../order/order.actions';
import { UpfrontPaymentMethod } from '@shared/enums/payment-type';
import { ProductClassifications } from '@core/enums/product-classifications';
import {
  AddShippingItemAction,
  RemoveShippingItemAction,
  LogisticsResetAction,
  SetSelectedDeliveryOptionAction,
  ApiGetDeliveryOptionsAction,
  ApiGetDeliveryOptionsSuccessAction,
  ApiGetDeliveryOptionsErrorAction,
  UpdateShippingItemAction,
  AddOrUpdateShippingItemAction,
} from './logistics.actions';
import { CustomerProductSelectors } from '../customer-product/customer-product.selectors';
import { VoiceProduct } from '@shared/models/voice-product.model';
import { CustomerProductMbbSelectors } from '@sales/modules/mbb/states/customer-product-mbb.selectors';
import { MbbProduct } from '@sales/modules/mbb/models/mbb-product.model';
import { NotificationService } from '@core/services/notification.service';

export interface LogisticsStateModel {
  items: ShippingItem[];
  isLoadingDeliveryOptions: boolean;
  selectedDeliveryOptionId: string;
  selectedOfferingId: string;
  deliveryOptions: DeliveryOptionDetails[];
}

const initialLogisticsState = {
  items: [],
  isLoadingDeliveryOptions: false,
  selectedDeliveryOptionId: null,
  selectedOfferingId: null,
  deliveryOptions: null,
};

const ErrorMessage = 'Feil ved henting av leveringsalternativer';

@State<LogisticsStateModel>({
  name: 'logistics',
  defaults: initialLogisticsState,
})
@Injectable()
export class LogisticsState {
  private logisticsService = inject(LogisticsService);
  private store = inject(Store);
  private notificationService = inject(NotificationService);

  @Action(AddOrUpdateShippingItemAction)
  public addOrUpdateShippingItem(
    { getState, dispatch }: StateContext<LogisticsStateModel>,
    { shippingItem }: AddOrUpdateShippingItemAction
  ) {
    const state = getState();
    const hasShippingItemChanged = state.items.find(
      existing => existing.id === shippingItem.id && existing.productClassification === shippingItem.productClassification
    );
    if (hasShippingItemChanged) {
      dispatch(new UpdateShippingItemAction(shippingItem));
    } else {
      dispatch(new AddShippingItemAction(shippingItem));
    }
  }

  @Action(AddShippingItemAction)
  public addShippingItem({ setState }: StateContext<LogisticsStateModel>, { shippingItem }: AddShippingItemAction) {
    setState(
      patch({
        items: insertItem(shippingItem),
        deliveryOptions: null,
        selectedDeliveryOptionId: null,
        selectedOfferingId: null,
      })
    );
  }

  @Action(UpdateShippingItemAction)
  public updateShippingItem(
    { setState, getState }: StateContext<LogisticsStateModel>,
    { shippingItem }: UpdateShippingItemAction
  ) {
    if (getState().items.find(item => item.id === shippingItem.id)) {
      setState(
        patch<LogisticsStateModel>({
          items: updateItem(
            item => item.id === shippingItem.id && item.productClassification === shippingItem.productClassification,
            patch(shippingItem)
          ),
          deliveryOptions: null,
          selectedDeliveryOptionId: null,
          selectedOfferingId: null,
        })
      );
    }
  }

  @Action(ApiGetDeliveryOptionsAction)
  public getDeliveryOptions({ dispatch, patchState, getState }: StateContext<LogisticsStateModel>) {
    const voiceProductsWithShipping = this.store.selectSnapshot(CustomerProductSelectors.voiceProductsWithShipping);
    const mbbProductsWithShipping = this.store.selectSnapshot(CustomerProductMbbSelectors.mbbProductsWithShipping);
    const otherProductsWithShipping = getState().items;
    const offeringIds = Array.from(
      new Set([
        ...voiceProductsWithShipping.map((voiceProduct: VoiceProduct) =>
          voiceProduct.isExistingSubscription ? null : voiceProduct.offering.id
        ),
        ...mbbProductsWithShipping.map((mbbProduct: MbbProduct) => mbbProduct.mbbSubscription.id),
        ...otherProductsWithShipping.map(item => item.offeringId),
      ])
    );

    if (!offeringIds || offeringIds.length === 0) {
      return;
    }

    const isOnlySimCardShipping =
      (voiceProductsWithShipping.length || mbbProductsWithShipping.length) && !otherProductsWithShipping.length;

    patchState({ isLoadingDeliveryOptions: true });

    const hasReturnBox = getState().items.some(item => item.productClassification === ProductClassifications.ReturnBox);
    if (!hasReturnBox && isOnlySimCardShipping) {
      return this.logisticsService.getSimCardOnlyOption().pipe(
        tap(response => dispatch(new ApiGetDeliveryOptionsSuccessAction([response]))),
        catchError(() => dispatch(new ApiGetDeliveryOptionsErrorAction(offeringIds)))
      );
    }

    return this.logisticsService.getDeliveryOptionsByOfferingIds(offeringIds, hasReturnBox).pipe(
      tap(response => dispatch(new ApiGetDeliveryOptionsSuccessAction(response))),
      catchError(() => dispatch(new ApiGetDeliveryOptionsErrorAction(offeringIds)))
    );
  }

  @Action(ApiGetDeliveryOptionsSuccessAction)
  public getDeliveryOptionsSuccess(
    { patchState }: StateContext<LogisticsStateModel>,
    { deliveryOptions }: ApiGetDeliveryOptionsSuccessAction
  ) {
    if (deliveryOptions.length === 1) {
      patchState({
        isLoadingDeliveryOptions: false,
        deliveryOptions: deliveryOptions,
        selectedDeliveryOptionId: deliveryOptions[0].deliveryOptionId,
        selectedOfferingId: deliveryOptions[0].offeringId,
      });
    } else {
      patchState({
        isLoadingDeliveryOptions: false,
        deliveryOptions: deliveryOptions,
        selectedDeliveryOptionId: null,
        selectedOfferingId: null,
      });
    }
  }

  @Action(ApiGetDeliveryOptionsErrorAction)
  public apiGetDeliveryOptionsError({ patchState }: StateContext<LogisticsStateModel>) {
    patchState({ isLoadingDeliveryOptions: false });
    this.notificationService.error(ErrorMessage);
  }

  @Action(RemoveShippingItemAction)
  public removeShippingItem(
    { setState, dispatch, getState }: StateContext<LogisticsStateModel>,
    { id, type }: RemoveShippingItemAction
  ) {
    const state = getState();
    if (!state.items.find(item => item.id === id && item.productClassification === type)) {
      return;
    }

    setState(
      patch<LogisticsStateModel>({
        items: state.items.filter(item => !(item.id === id && item.productClassification === type)),
        deliveryOptions: null,
        selectedDeliveryOptionId: null,
        selectedOfferingId: null,
      })
    );

    const remainingItems = state.items.filter(item => !(item.id === id && item.productClassification === type));

    if (remainingItems.length === 0) {
      dispatch(new SetOrderPaymentTypeAction(UpfrontPaymentMethod.NoUpfrontPayment));
    }

    dispatch(new ApiGetDeliveryOptionsAction());
  }

  @Action(SetSelectedDeliveryOptionAction)
  public setDeliveryOptionForItem(
    { patchState }: StateContext<LogisticsStateModel>,
    { deliveryOptionId, offeringId }: SetSelectedDeliveryOptionAction
  ) {
    patchState({ selectedDeliveryOptionId: deliveryOptionId, selectedOfferingId: offeringId });
  }

  @Action(LogisticsResetAction)
  public logisticsReset({ patchState }: StateContext<LogisticsStateModel>) {
    patchState(initialLogisticsState);
  }
}
